import React, { useState } from 'react';
import {
    Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';

import { URLS } from 'services/urls';
import { useAuthContext } from 'contexts/AuthContext';
import { useGetOrganizationAddons } from 'hooks/useGetOrganizationAddons';
import RenderIf from 'components/RenderIf/RenderIf';
import { FEATURES_LIST } from 'services/constants';
import { useOrganizationAccess } from 'hooks/useOrganizationAccess';
import '../../NSNavbar.scss';
import { NSBadge } from 'bricks';
import FeatureTierPopover from '../FeatureTierPopOver/FeatureTierPopover';
import { PORTFOLIO_ANALYTICS_PRO_ADDON_ID } from './constants';

const AnalyticsMenu: React.FC = () => {
    const [isAnalyticsMenuOpen, setIsAnalyticsMenuOpen] = useState(false);
    const toggleAnalyticsMenu = () => setIsAnalyticsMenuOpen(!isAnalyticsMenuOpen);

    const { selectedOrganizationId } = useAuthContext();
    const { data: addons, isLoading } = useGetOrganizationAddons(selectedOrganizationId!);
    const {
        hasActiveEnterpriseProjects,
        hasActiveLiteProjects,
        hasActiveProProjects,
        hasActivePlanPlusProjects,
        hasActivePlanProjects,
    } = useOrganizationAccess();

    const [popoverState, setPopoverState] = useState({
        costAnalytics: false,
        globalCost: false,
        portfolioCapitalPlanning: false,
        vendorAnalytics: false,
        quickAnalytics: false,
        budgetCategorizations: false,
    });

    const togglePopover = (key: keyof typeof popoverState) => {
        setPopoverState(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const isPAPEnabled = !isLoading && addons?.some(addon => addon.id === PORTFOLIO_ANALYTICS_PRO_ADDON_ID);
    const upgradeTier = hasActiveEnterpriseProjects ? 'Enterprise' : 'Pro';

    return (
        <Dropdown
            isOpen={isAnalyticsMenuOpen}
            toggle={toggleAnalyticsMenu}
            onMouseEnter={() => setIsAnalyticsMenuOpen(true)}
            onMouseLeave={() => setIsAnalyticsMenuOpen(false)}
            className="d-flex align-items-center px-2"
            data-testid="analytics-menu-dropdown"
        >
            <DropdownToggle tag="span" className="NSNavbar__dropdown_toggle" data-testid="analytics-menu-toggle">
                Analytics
                <FontAwesomeIcon className="icon ml-2" icon={faCaretDown} />
            </DropdownToggle>

            {!isLoading && isAnalyticsMenuOpen && (
                <DropdownMenu className="NSNavbar__dropdown_Menu">
                    <RenderIf isTrue={hasActiveProProjects || hasActiveEnterpriseProjects}>
                        <DropdownItem className="text-wrap mt-1">
                            <a href={URLS.PROJECTS_APP.COST_ANALYTICS} className="NSNavbar__dropdown_link">Cost Analytics</a>
                        </DropdownItem>

                        <RenderIf isTrue={isPAPEnabled}>
                            <DropdownItem className="text-wrap">
                                <a href={URLS.PROJECTS_APP.DEVELOPER_FEE} className="NSNavbar__dropdown_link">Developer Fee</a>
                            </DropdownItem>
                        </RenderIf>

                        <DropdownItem className="text-wrap">
                            <a
                                href={isPAPEnabled ? URLS.PROJECTS_APP.GLOBAL_COST_FORECAST_V2 : URLS.PROJECTS_APP.GLOBAL_COST_FORECAST}
                                className="NSNavbar__dropdown_link"
                            >
                                Global Cost Forecast
                            </a>
                        </DropdownItem>

                        <RenderIf isTrue={isPAPEnabled}>
                            <DropdownItem className="text-wrap">
                                <a href={URLS.PROJECTS_APP.PORTFOLIO_CAPITAL_PLANNING} className="NSNavbar__dropdown_link">
                                    Portfolio Capital Planning
                                </a>
                            </DropdownItem>
                        </RenderIf>

                        <DropdownItem className="text-wrap">
                            <a href={URLS.PROJECTS_APP.VENDOR_ANALYTICS} className="NSNavbar__dropdown_link">Vendor Analytics</a>
                        </DropdownItem>

                        <DropdownItem className="text-wrap">
                            <a href={URLS.PROJECTS_APP.QUICK_ANALYTICS} className="NSNavbar__dropdown_link">Quick Analytics</a>
                        </DropdownItem>

                        <DropdownItem className="text-wrap">
                            <a href={URLS.PROJECTS_APP.PORTFOLIO_BUDGET_CATEGORIZATIONS} className="NSNavbar__dropdown_link">
                                Portfolio Budget Categorizations
                            </a>
                        </DropdownItem>
                    </RenderIf>

                    <RenderIf isTrue={!hasActiveProProjects && !hasActiveEnterpriseProjects && (hasActivePlanProjects || hasActivePlanPlusProjects)}>
                        <DropdownItem id="cost-analytics-popover-link">
                            Cost Analytics
                        </DropdownItem>

                        <RenderIf isTrue={isPAPEnabled}>
                            <DropdownItem id="developer-fee-popover-link">
                                Developer Fee
                            </DropdownItem>
                        </RenderIf>

                        <div
                            onClick={() => togglePopover('globalCost')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    togglePopover('globalCost');
                                }
                            }}
                        >
                            <DropdownItem id="global-cost-popover-link" disabled>
                                Global Cost Forecast
                                <NSBadge color="primary-lighten" className="ml-2">{upgradeTier}</NSBadge>
                            </DropdownItem>
                            <FeatureTierPopover
                                isOpen={popoverState.globalCost}
                                toggle={() => togglePopover('globalCost')}
                                target="global-cost-popover-link"
                                feature={FEATURES_LIST.GLOBAL_COST_FORECAST}
                                upgradeTier={upgradeTier}
                                placement="bottom"
                            />
                        </div>

                        <RenderIf isTrue={isPAPEnabled}>
                            <DropdownItem id="portfolio-capital-planning-popover-link">
                                Portfolio Capital Planning
                            </DropdownItem>
                        </RenderIf>

                        <DropdownItem className="text-wrap">
                            <a href={URLS.PROJECTS_APP.VENDOR_ANALYTICS} className="NSNavbar__dropdown_link">Vendor Analytics</a>
                        </DropdownItem>

                        <DropdownItem id="quick-analytics-popover-link">
                            Quick Analytics
                        </DropdownItem>

                        <div
                            onClick={() => togglePopover('budgetCategorizations')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    togglePopover('budgetCategorizations');
                                }
                            }}
                        >
                            <DropdownItem id="budget-categorizations-popover-link" disabled>
                                Portfolio Budget Categorizations
                                <NSBadge color="primary-lighten" className="ml-2">{upgradeTier}</NSBadge>
                            </DropdownItem>
                            <FeatureTierPopover
                                isOpen={popoverState.budgetCategorizations}
                                toggle={() => togglePopover('budgetCategorizations')}
                                target="budget-categorizations-popover-link"
                                feature={FEATURES_LIST.PORTFOLIO_BUDGET_CATEGORIZATIONS}
                                upgradeTier={upgradeTier}
                                placement="bottom"
                            />
                        </div>
                    </RenderIf>

                    <RenderIf isTrue={
                        !hasActiveProProjects
                        && !hasActiveEnterpriseProjects
                        && !hasActivePlanProjects
                        && !hasActivePlanPlusProjects
                        && hasActiveLiteProjects
                    }
                    >
                        <DropdownItem id="cost-analytics-popover-link">
                            Cost Analytics
                        </DropdownItem>

                        <RenderIf isTrue={isPAPEnabled}>
                            <DropdownItem id="developer-fee-popover-link">
                                Developer Fee
                            </DropdownItem>
                        </RenderIf>

                        <div
                            onClick={() => togglePopover('globalCost')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    togglePopover('globalCost');
                                }
                            }}
                        >
                            <DropdownItem id="global-cost-popover-link" disabled>
                                Global Cost Forecast
                                <NSBadge color="primary-lighten" className="ml-2">{upgradeTier}</NSBadge>
                            </DropdownItem>
                            <FeatureTierPopover
                                isOpen={popoverState.globalCost}
                                toggle={() => togglePopover('globalCost')}
                                target="global-cost-popover-link"
                                feature={FEATURES_LIST.GLOBAL_COST_FORECAST}
                                upgradeTier={upgradeTier}
                                placement="bottom"
                            />
                        </div>

                        <RenderIf isTrue={isPAPEnabled}>
                            <DropdownItem id="portfolio-capital-planning-popover-link">
                                Portfolio Capital Planning
                            </DropdownItem>
                        </RenderIf>

                        <DropdownItem className="text-wrap">
                            <a href={URLS.PROJECTS_APP.VENDOR_ANALYTICS} className="NSNavbar__dropdown_link">Vendor Analytics</a>
                        </DropdownItem>

                        <div
                            onClick={() => togglePopover('quickAnalytics')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    togglePopover('quickAnalytics');
                                }
                            }}
                        >
                            <DropdownItem id="quick-analytics-popover-link" disabled>
                                Quick Analytics
                                <NSBadge color="primary-lighten" className="ml-2">{upgradeTier}</NSBadge>
                            </DropdownItem>
                            <FeatureTierPopover
                                isOpen={popoverState.quickAnalytics}
                                toggle={() => togglePopover('quickAnalytics')}
                                target="quick-analytics-popover-link"
                                feature={FEATURES_LIST.QUICK_ANALYTICS}
                                upgradeTier={upgradeTier}
                                placement="bottom"
                            />
                        </div>

                        <div
                            onClick={() => togglePopover('budgetCategorizations')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    togglePopover('budgetCategorizations');
                                }
                            }}
                        >
                            <DropdownItem id="budget-categorizations-popover-link" disabled>
                                Portfolio Budget Categorizations
                                <NSBadge color="primary-lighten" className="ml-2">{upgradeTier}</NSBadge>
                            </DropdownItem>
                            <FeatureTierPopover
                                isOpen={popoverState.budgetCategorizations}
                                toggle={() => togglePopover('budgetCategorizations')}
                                target="budget-categorizations-popover-link"
                                feature={FEATURES_LIST.PORTFOLIO_BUDGET_CATEGORIZATIONS}
                                upgradeTier={upgradeTier}
                                placement="bottom"
                            />
                        </div>
                    </RenderIf>

                    <RenderIf isTrue={
                        !hasActiveProProjects
                        && !hasActiveEnterpriseProjects
                        && !hasActivePlanProjects
                        && !hasActivePlanPlusProjects
                        && !hasActiveLiteProjects
                    }
                    >
                        <div
                            onClick={() => togglePopover('costAnalytics')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    togglePopover('costAnalytics');
                                }
                            }}
                        >
                            <DropdownItem id="cost-analytics-popover-link" disabled>
                                Cost Analytics
                                <NSBadge color="primary-lighten" className="ml-2">{upgradeTier}</NSBadge>
                            </DropdownItem>
                            <FeatureTierPopover
                                isOpen={popoverState.costAnalytics}
                                toggle={() => togglePopover('costAnalytics')}
                                target="cost-analytics-popover-link"
                                feature={FEATURES_LIST.PORTFOLIO_COST_ANALYTICS}
                                upgradeTier={upgradeTier}
                                placement="bottom"
                            />
                        </div>

                        <RenderIf isTrue={isPAPEnabled}>
                            <DropdownItem id="developer-fee-popover-link">
                                Developer Fee
                            </DropdownItem>
                        </RenderIf>

                        <div
                            onClick={() => togglePopover('globalCost')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    togglePopover('globalCost');
                                }
                            }}
                        >
                            <DropdownItem id="global-cost-popover-link" disabled>
                                Global Cost Forecast
                                <NSBadge color="primary-lighten" className="ml-2">{upgradeTier}</NSBadge>
                            </DropdownItem>
                            <FeatureTierPopover
                                isOpen={popoverState.globalCost}
                                toggle={() => togglePopover('globalCost')}
                                target="global-cost-popover-link"
                                feature={FEATURES_LIST.GLOBAL_COST_FORECAST}
                                upgradeTier={upgradeTier}
                                placement="bottom"
                            />
                        </div>

                        <RenderIf isTrue={isPAPEnabled}>
                            <DropdownItem id="portfolio-capital-planning-popover-link">
                                Portfolio Capital Planning
                            </DropdownItem>
                        </RenderIf>

                        <div
                            onClick={() => togglePopover('vendorAnalytics')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    togglePopover('vendorAnalytics');
                                }
                            }}
                        >
                            <DropdownItem id="vendor-analytics-popover-link" disabled>
                                Vendor Analytics
                                <NSBadge color="primary-lighten" className="ml-2">{upgradeTier}</NSBadge>
                            </DropdownItem>
                            <FeatureTierPopover
                                isOpen={popoverState.vendorAnalytics}
                                toggle={() => togglePopover('vendorAnalytics')}
                                target="vendor-analytics-popover-link"
                                feature={FEATURES_LIST.PORTFOLIO_VENDOR_ANALYTICS}
                                upgradeTier={upgradeTier}
                                placement="bottom"
                            />
                        </div>

                        <div
                            onClick={() => togglePopover('quickAnalytics')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    togglePopover('quickAnalytics');
                                }
                            }}
                        >
                            <DropdownItem id="quick-analytics-popover-link" disabled>
                                Quick Analytics
                                <NSBadge color="primary-lighten" className="ml-2">{upgradeTier}</NSBadge>
                            </DropdownItem>
                            <FeatureTierPopover
                                isOpen={popoverState.quickAnalytics}
                                toggle={() => togglePopover('quickAnalytics')}
                                target="quick-analytics-popover-link"
                                feature={FEATURES_LIST.QUICK_ANALYTICS}
                                upgradeTier={upgradeTier}
                                placement="bottom"
                            />
                        </div>

                        <div
                            onClick={() => togglePopover('budgetCategorizations')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    togglePopover('budgetCategorizations');
                                }
                            }}
                        >
                            <DropdownItem id="budget-categorizations-popover-link" disabled>
                                Portfolio Budget Categorizations
                                <NSBadge color="primary-lighten" className="ml-2">{upgradeTier}</NSBadge>
                            </DropdownItem>
                            <FeatureTierPopover
                                isOpen={popoverState.budgetCategorizations}
                                toggle={() => togglePopover('budgetCategorizations')}
                                target="budget-categorizations-popover-link"
                                feature={FEATURES_LIST.PORTFOLIO_BUDGET_CATEGORIZATIONS}
                                upgradeTier={upgradeTier}
                                placement="bottom"
                            />
                        </div>
                    </RenderIf>
                </DropdownMenu>
            )}
        </Dropdown>
    );
};

export default AnalyticsMenu;
